/* ---------------------------------------------------------------------------
   Referencias de mercado — capa HTTP y tipos.

   El módulo enlaza evidencia de mercado (transacciones del CBR y ofertas de
   portales) a un informe. Este archivo cubre el PASO 1: elegir el informe.

   Los decimales llegan como STRING desde PHP (`decimal:2`) y acá NO se
   recalculan: se formatean. Es la misma regla del resto del webapp.
--------------------------------------------------------------------------- */

import { apiFetch } from "@/lib/api";

/**
 * Los tres estados en los que un informe admite referencias: de la aceptación
 * en adelante.
 *
 * Es el gemelo de `Informe::ESTADOS_CON_TASACION` del backend, que responde 422
 * a cualquier otro estado. Antes de aceptar el informe es todavía un
 * PRESUPUESTO: el cliente no dijo que sí y la tasación puede no ocurrir, así que
 * enlazarle comparables sería trabajo sobre algo que quizá se anule.
 */
export const ESTADOS_CON_TASACION = [
  "en_proceso",
  "en_espera_revision_final",
  "finalizado",
] as const;

export type EstadoConTasacion = (typeof ESTADOS_CON_TASACION)[number];

/**
 * Si a este informe se le pueden enlazar referencias de mercado.
 *
 * Recibe el estado como `string` y no como `EstadoInforme` para no atar este
 * archivo al de informes: acá lo que importa es el subconjunto, no el
 * vocabulario entero.
 */
export function admiteReferenciasDeMercado(estado: string): estado is EstadoConTasacion {
  return (ESTADOS_CON_TASACION as readonly string[]).includes(estado);
}

/**
 * Cómo se rotula cada estado en los chips del filtro.
 *
 * Vocabulario CORTO y propio de esta pantalla: el chip vive al lado de un
 * buscador y tres selects, y "En espera de revisión final" no entra. El nombre
 * largo sigue siendo el del informe en su ficha.
 */
/**
 * La etiqueta CORTA, para la columna Estado de la tabla.
 *
 * El encabezado ya dice ESTADO, así que la preposición de "En proceso" solo
 * gasta ancho en una columna angosta. Los chips del filtro sí usan la larga:
 * ahí no hay encabezado que dé el contexto.
 */
export const ETIQUETA_CORTA_ESTADO: Record<EstadoConTasacion, string> = {
  en_proceso: "Proceso",
  en_espera_revision_final: "Revisión",
  finalizado: "Aprobado",
};

export const ETIQUETA_ESTADO: Record<EstadoConTasacion, string> = {
  en_proceso: "En proceso",
  en_espera_revision_final: "En revisión",
  finalizado: "Aprobado",
};

/** El rol y la comuna de un predio del informe, tal como el informe los congeló. */
export interface RolDelInforme {
  rol_sii: string;
  comuna: string | null;
  comuna_codigo: string | null;
}

/** Una fila de la tabla del paso 1. */
export interface InformeParaReferencias {
  id: number;
  numero_informe: string | null;
  propiedad_nombre: string | null;
  cliente_nombre: string | null;
  estado: EstadoConTasacion;
  superficie_ha: string | null;
  unidad_superficie: string | null;
  /** El tipo de tasación ES la plantilla del informe: su nombre. */
  plantilla: string | null;
  /** El nombre del formato de salida elegido (reemplazó al uso de la tasación). */
  formato_salida: string | null;
  /** Cuántas tiene enlazadas, SIN contar las de la papelera. */
  referencias: number;
  roles?: RolDelInforme[];
  ejecutivo?: { id: number; first_name: string; last_name: string } | null;
  /**
   * Si el predio tiene coordenada. Sin ella el paso 2 no puede ordenar por
   * distancia, y la pantalla lo avisa ANTES de continuar en vez de dejar que
   * el usuario lo descubra allá.
   */
  tiene_coordenada: boolean;
  actualizado_en: string | null;
}

export interface FiltrosDeInformesParaReferencias {
  buscar?: string;
  estado?: EstadoConTasacion;
  ejecutivoId?: number;
  comunaCodigo?: string;
  cliente?: string;
  /** Ventana de última actualización en días. `0` es el histórico. */
  actualizacion?: number;
  superficieMin?: number;
  superficieMax?: number;
  sinReferencias?: boolean;
  orden?: "actualizado" | "sin_referencias" | "superficie" | "cliente";
  pagina?: number;
  /**
   * Cuántos informes trae el lote.
   *
   * Es como funciona "cargar más": la pantalla NO pasa a la página 2, pide un
   * lote más grande y vuelve a dibujar la lista entera. Así no hay que
   * concatenar páginas ni decidir qué pasa cuando el orden cambia entre una y
   * la siguiente. Mismo mecanismo que el catálogo del paso 2.
   */
  porPagina?: number;
}

export interface PaginaDeInformesParaReferencias {
  informes: InformeParaReferencias[];
  /** Los que dejó el filtro. */
  total: number;
  /** Los que hay en el sistema, sin filtrar. El encabezado dice este. */
  totalGeneral: number;
  pagina: number;
  porPagina: number;
  totalPaginas: number;
}

interface RespuestaPaginada {
  data: InformeParaReferencias[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    total_general: number;
  };
}

export async function fetchInformesParaReferencias(
  filtros: FiltrosDeInformesParaReferencias = {},
): Promise<PaginaDeInformesParaReferencias> {
  const parametros = new URLSearchParams();

  // Solo lo que el usuario eligió: mandar una clave vacía haría que el backend
  // la tome por presente y valide un valor que no lo es — y un filtro inválido
  // da 422 a propósito, no "sin filtro".
  if (filtros.buscar?.trim()) parametros.set("buscar", filtros.buscar.trim());
  if (filtros.estado) parametros.set("estado", filtros.estado);
  if (filtros.ejecutivoId) parametros.set("ejecutivo_id", String(filtros.ejecutivoId));
  if (filtros.comunaCodigo) parametros.set("comuna_codigo", filtros.comunaCodigo);
  if (filtros.cliente?.trim()) parametros.set("cliente", filtros.cliente.trim());
  // `0` es el histórico y SÍ se manda: es una elección del usuario, no un
  // valor vacío. Por eso se compara contra `undefined` y no por verdad.
  if (filtros.actualizacion !== undefined) {
    parametros.set("actualizacion", String(filtros.actualizacion));
  }
  if (filtros.superficieMin !== undefined) {
    parametros.set("superficie_min", String(filtros.superficieMin));
  }
  if (filtros.superficieMax !== undefined) {
    parametros.set("superficie_max", String(filtros.superficieMax));
  }
  if (filtros.sinReferencias) parametros.set("sin_referencias", "1");
  if (filtros.orden) parametros.set("orden", filtros.orden);
  if (filtros.pagina && filtros.pagina > 1) parametros.set("page", String(filtros.pagina));
  if (filtros.porPagina) parametros.set("por_pagina", String(filtros.porPagina));

  const cadena = parametros.toString();
  const respuesta = await apiFetch<RespuestaPaginada>(
    `/api/informes/para-referencias${cadena ? `?${cadena}` : ""}`,
  );

  return {
    informes: respuesta.data,
    total: respuesta.meta.total,
    totalGeneral: respuesta.meta.total_general,
    pagina: respuesta.meta.current_page,
    porPagina: respuesta.meta.per_page,
    totalPaginas: respuesta.meta.last_page,
  };
}

/* ---------------------------------------------------------------------------
   Los CONTROLES del filtro del paso 1.

   Van en su propio pedido y no en el `meta` del listado por dos motivos
   distintos: no se pueden armar con la página —son lotes de seis, así que la
   comuna del informe 40 no estaría en el selector hasta cargar cuatro lotes— y
   no cambian con el filtro, así que se piden una vez en lugar de recalcularse
   en cada tecla del buscador.
--------------------------------------------------------------------------- */

export interface CatalogosDeInformesParaReferencias {
  /**
   * Cuántos informes hay en cada estado, sobre el alcance ENTERO y no sobre lo
   * filtrado: el número del chip existe para decir cuántos hay ANTES de
   * apretarlo. Los tres vienen siempre, aunque valgan cero.
   */
  conteo_por_estado: Record<EstadoConTasacion, number>;
  comunas: { codigo: string; nombre: string }[];
  clientes: string[];
  ejecutivos: { id: number; nombre: string }[];
  /**
   * Hasta dónde llega la escala del slider de superficie. `null` cuando ningún
   * informe declaró la suya: ahí no hay escala que dibujar y el control se
   * apaga en vez de ofrecer un rango de 0 a 0.
   */
  superficie_maxima: number | null;
}

export async function fetchCatalogosDeInformesParaReferencias(): Promise<CatalogosDeInformesParaReferencias> {
  return apiFetch<CatalogosDeInformesParaReferencias>(
    "/api/informes/para-referencias/catalogos",
  );
}

/**
 * Cómo se nombra un informe en una línea.
 *
 * El predio primero porque es lo que el tasador reconoce; el número de informe
 * queda de respaldo para los que todavía no tienen nombre de predio.
 */
export function describirInforme(informe: InformeParaReferencias): string {
  return informe.propiedad_nombre?.trim() || informe.numero_informe?.trim() || "Sin nombre";
}

/**
 * Las comunas del informe, sin repetir.
 *
 * Un informe puede tener varios roles y varios caer en la misma comuna: la
 * tabla muestra una sola vez cada una.
 */
export function comunasDelInforme(informe: InformeParaReferencias): string[] {
  const nombres = (informe.roles ?? [])
    .map((rol) => rol.comuna?.trim())
    .filter((nombre): nombre is string => Boolean(nombre));

  return [...new Set(nombres)];
}

/* ---------------------------------------------------------------------------
   PASO 2: el catálogo de mercado y las referencias enlazadas.

   Dos vocabularios que NO son el mismo y conviene no mezclar:

     * un COMPARABLE es una ficha viva del catálogo, que vive en otra base y
       este sistema solo lee;
     * una REFERENCIA es la COPIA que un informe congeló de un comparable, más
       la decisión que el tasador tomó sobre ella.

   Se parecen mucho y se separan a propósito: el día que el catálogo corrija un
   precio, la referencia del informe no se mueve.
--------------------------------------------------------------------------- */

/** Las dos ramas de la evidencia de mercado, que son las dos pestañas. */
export type ClaseDeEvidencia = "transaccion" | "oferta";

export const ETIQUETA_CLASE: Record<ClaseDeEvidencia, string> = {
  transaccion: "Transacciones CBR",
  oferta: "Ofertas de mercado",
};

/** Una ficha del catálogo, tal como la muestra la tarjeta del paso 2. */
export interface Comparable {
  clase: ClaseDeEvidencia;
  origen_id: number;
  titulo: string;
  /**
   * La prosa con que el aviso se publicó.
   *
   * `null` en toda transacción del CBR: sale de un archivo del SII y no tiene
   * aviso. No es un dato faltante — es que no existe, y por eso la tarjeta
   * omite el bloque en vez de dibujarlo vacío.
   */
  descripcion: string | null;
  comuna: string;
  comuna_codigo: string | null;
  fuente: string;
  referencia: string | null;
  url: string | null;
  superficie_terreno_m2: number | null;
  superficie_construida_m2: number | null;
  precio_uf: number | null;
  precio_monto: number | null;
  precio_moneda: string | null;
  uf_ha_terreno: number | null;
  fecha: string | null;
  dias_en_mercado: number | null;
  latitud: number | null;
  longitud: number | null;
  distancia_m: number | null;
  precision_ubicacion: string | null;
  /**
   * Si la ubicación es el centro de la comuna en vez de un punto conocido. Lo
   * responde el servidor para que la pantalla no tenga que conocer el
   * vocabulario de precisiones del catálogo.
   */
  ubicacion_estimada: boolean;
  /**
   * La clase de uso de suelo DOMINANTE por superficie ("4", "3R"...).
   *
   * Es la que usa el FILTRO del catálogo, porque necesita un valor único
   * contra el cual comparar — no es la que se imprime en la ficha, ver
   * `clase_suelo_detalle`.
   *
   * `null` en TODA oferta —un aviso de portal no tiene rol, así que no llega a
   * ningún predio— y en la mayoría de las transacciones: de 70.046 solo 5.998
   * tienen suelo cargado, porque solo el rol AGRÍCOLA lo publica.
   */
  clase_suelo: string | null;
  /**
   * TODAS las clases del predio, ya ordenadas y separadas por ", " ("3R, 4").
   *
   * Es lo que la ficha IMPRIME y no la dominante: de las fichas con clase, 69 %
   * tiene una sola, 22 % dos y 9 % entre tres y seis — mostrar solo la
   * dominante afirmaría que un predio mixto es uniforme. Mismo `null` que
   * `clase_suelo`, por el mismo motivo.
   */
  clase_suelo_detalle: string | null;
}

/**
 * Cuánto corrige un ajuste de homologación a esta referencia, YA CONGELADO.
 *
 * Viaja el `categoria_id` además de la copia porque el selector tiene que poder
 * mostrar lo elegido marcado, y el nombre no alcanza: es editable desde el
 * mantenedor, así que emparejar por texto se rompe el día que alguien corrija
 * la escala.
 *
 * `categoria` y `porcentaje` son la copia del día en que se eligió, no el valor
 * vivo del catálogo. El informe se firma con esta cifra.
 */
export interface AjusteDeReferencia {
  ajuste_id: number;
  categoria_id: number;
  categoria: string;
  porcentaje: number;
}

/** Una referencia ya enlazada: la copia congelada más su estado. */
export interface ReferenciaDelInforme {
  id: number;
  clase: ClaseDeEvidencia;
  origen_id: number;
  url: string | null;
  titulo: string;
  /**
   * La prosa del aviso, CONGELADA el día que se enlazó. El aviso se edita y se
   * baja; el informe se firma. `null` en las transacciones del CBR y en toda
   * referencia enlazada antes de que esta columna existiera.
   */
  descripcion: string | null;
  comuna: string;
  comuna_codigo: string | null;
  fuente: string;
  referencia: string | null;
  superficie_terreno_m2: string | null;
  superficie_construida_m2: string | null;
  precio_uf: string | null;
  precio_monto: string | null;
  precio_moneda: string | null;
  uf_valor_clp: string | null;
  uf_fecha: string | null;
  fecha: string | null;
  dias_en_mercado: number | null;
  latitud: string | null;
  longitud: string | null;
  precision_ubicacion: string | null;
  distancia_m: number | null;
  validada: boolean;
  validado_en: string | null;
  validado_por?: { id: number; first_name: string; last_name: string } | null;
  descartada: boolean;
  descartado_en: string | null;
  enlazado_en: string | null;
  /**
   * OPCIONAL porque el backend solo la manda con la relación cargada
   * (`whenLoaded`): sin eso, una lista de treinta referencias haría treinta
   * consultas más. Llega en el listado del informe y en la respuesta de
   * guardar; no llega, por ejemplo, al enlazar una ficha nueva.
   */
  ajustes?: AjusteDeReferencia[];
  /**
   * La clase de uso de suelo DOMINANTE, CONGELADA el día que se enlazó. Ver
   * `Comparable.clase_suelo`. `null` también en toda referencia enlazada
   * ANTES de que estas dos columnas existieran: es el estado normal, no un
   * dato que falte.
   */
  clase_suelo: string | null;
  /**
   * El desglose completo, congelado igual que la dominante. Es lo que la
   * ficha imprime — ver `Comparable.clase_suelo_detalle`.
   */
  clase_suelo_detalle: string | null;
}

/**
 * Cómo se rotula la clase de uso de suelo en la línea de metadatos de las dos
 * fichas.
 *
 * SE LEE EL DESGLOSE Y NO LA DOMINANTE (ver el comentario de
 * `Comparable.clase_suelo`): singular o plural se decide por si ese desglose
 * trae una coma, que es como se distingue un predio de una sola clase de uno
 * mixto. Función compartida y no una copia por ficha, porque el repo ya tuvo
 * el problema de `Metrica` duplicada entre `FichaDeComparable` y
 * `FichaDeReferencia`.
 *
 * `null` cuando la ficha no tiene clase —la mayoría del catálogo—, y ahí no
 * hay nada que rotular: ni el texto ni un guion, que sería una fila vacía
 * repetida en casi todas las tarjetas.
 */
export function describirClaseDeSuelo(detalle: string | null): string | null {
  if (!detalle) return null;

  return detalle.includes(",") ? `Clases ${detalle}` : `Clase ${detalle}`;
}

export type OrdenDelCatalogo = "distancia" | "valor_unitario" | "mas_recientes" | "superficie";

export interface FiltrosDelCatalogo {
  clase?: ClaseDeEvidencia;
  comunas?: string[];
  fuentes?: string[];
  /**
   * La clase de uso de suelo del predio (catálogo del SII), por su CÓDIGO —
   * "3R" es la clase 3 con riego, y ese código es lo que se imprime.
   *
   * SOLO existe para transacciones: una oferta de portal no tiene rol, así
   * que no llega a ningún predio y jamás tiene clase de suelo (medido: 0 de
   * 4.473 ofertas). La pantalla oculta el control en esa pestaña en vez de
   * mandarlo siempre vacío.
   */
  clasesDeSuelo?: string[];
  /**
   * El año de la operación, como RANGO y no como lista de años sueltos.
   *
   * Fue una lista hasta el 08-09-2026. Elegir años salteados —2024 sí, 2023 no,
   * 2022 sí— no es algo que se le pida a la evidencia de mercado: lo que se
   * acota es una ventana reciente. Lo que costaba era una fila de diecisiete
   * casillas que empujaba el resto de los filtros fuera de la vista.
   */
  anioDesde?: number;
  anioHasta?: number;
  superficieMinHa?: number;
  superficieMaxHa?: number;
  /**
   * La banda de VALOR UNITARIO, en UF por hectárea.
   *
   * En la MISMA unidad que el catálogo, al revés que la superficie y la
   * distancia: no hay nada que convertir, y es justamente lo que vuelve
   * comparables predios de tamaños distintos.
   */
  valorMinUfHa?: number;
  valorMaxUfHa?: number;
  /**
   * En KILÓMETROS, que es como habla la pantalla. El servidor convierte a
   * metros: convertir acá lo volvería un segundo lugar donde se decide qué
   * significa "10 km".
   */
  distanciaMaxKm?: number;
  /**
   * "Incluir fichas con datos incompletos": el servidor deja de descartar a
   * quien no publica el dato que un filtro pregunta.
   *
   * Con una banda de superficie o un rango de años puestos, entran también las
   * fichas sin superficie o sin fecha; y entran las ubicadas en el centro de su
   * comuna —salvo con un filtro de distancia, donde su distancia sería falsa—.
   * Va a TODAS las consultas del mismo conjunto: listado, puntos, contadores de
   * los selectores y enlace. Apagada, la clave no viaja.
   */
  incluirIncompletas?: boolean;
  orden?: OrdenDelCatalogo;
  pagina?: number;
  porPagina?: number;
}

export interface PaginaDelCatalogo {
  fichas: Comparable[];
  total: number;
  pagina: number;
  porPagina: number;
  quedan: number;
  /**
   * La mediana de UF/ha del FILTRO ENTERO, no la de estas fichas.
   *
   * Es la línea del gráfico y el cero contra el que cada tarjeta dibuja su
   * barra, y las dos cosas se rotulan "del filtro". No se puede derivar acá:
   * la página son seis fichas y el filtro puede ser trece mil. La mide el
   * catálogo, en la misma pasada con que cuenta el total.
   */
  medianaUfHa: number | null;
  uf: { valor: number; fecha: string } | null;
  clase: ClaseDeEvidencia;
  /**
   * Los `origen_id` de ESTA clase que el informe ya dio por buenos sin
   * agregarlos todavía.
   *
   * Viaja la lista COMPLETA y no solo la de esta página: el panel derecho
   * cuenta cuántas se llevan revisadas, y una lista por página diría "2
   * validadas" en la primera y "0" en la segunda.
   */
  validadas: number[];
  /**
   * Cuántas hay en la papelera, contando LAS DOS CLASES: es una sola lista con
   * las fichas de las dos pestañas, así que un número por pestaña mentiría
   * sobre cuántas hay adentro.
   */
  enLaPapelera: number;
}

export interface CatalogosDelPaso2 {
  clase: ClaseDeEvidencia;
  comunas: { codigo: string; nombre: string; fichas: number }[];
  fuentes: { clave: string; fichas: number }[];
  /**
   * La clase de uso de suelo del catálogo, con su código y cuántas fichas la
   * tienen.
   *
   * VIENE EN EL ORDEN DE LA ESCALA (`1, 1R, 2, 2R, …, 8`) y no por cantidad,
   * al revés que `fuentes`: es la capacidad de uso, de 1 (el mejor suelo
   * arable) a 8 (el inutilizable), así que ordenarla por frecuencia
   * desordenaría una escala. El sufijo `R` marca el suelo REGADO de esa misma
   * clase y va pegado a su base. La sirve así el servidor — la pantalla no
   * reordena.
   *
   * Va vacía en la pestaña de ofertas y ahí el selector ni se dibuja: ver
   * `FiltrosDelCatalogo.clasesDeSuelo`.
   */
  clases_de_suelo: { codigo: string; fichas: number }[];
  anios: number[];
  uf: { valor: number; fecha: string } | null;
  /** Con su etiqueta ya redactada: la pantalla no traduce enums del servidor. */
  ordenes: { clave: OrdenDelCatalogo; etiqueta: string }[];
}

function parametrosDelCatalogo(filtros: FiltrosDelCatalogo): string {
  const p = new URLSearchParams();

  if (filtros.clase) p.set("clase", filtros.clase);
  for (const comuna of filtros.comunas ?? []) p.append("comunas[]", comuna);
  for (const fuente of filtros.fuentes ?? []) p.append("fuentes[]", fuente);
  for (const clase of filtros.clasesDeSuelo ?? []) p.append("clases_de_suelo[]", clase);

  if (filtros.anioDesde !== undefined) p.set("anio_desde", String(filtros.anioDesde));
  if (filtros.anioHasta !== undefined) p.set("anio_hasta", String(filtros.anioHasta));

  // `!== undefined` y no por verdad: un mínimo de 0 hectáreas es una elección
  // válida del slider, y `if (0)` la descartaría.
  if (filtros.superficieMinHa !== undefined) {
    p.set("superficie_min_ha", String(filtros.superficieMinHa));
  }
  if (filtros.superficieMaxHa !== undefined) {
    p.set("superficie_max_ha", String(filtros.superficieMaxHa));
  }
  if (filtros.valorMinUfHa !== undefined) {
    p.set("valor_min_uf_ha", String(filtros.valorMinUfHa));
  }
  if (filtros.valorMaxUfHa !== undefined) {
    p.set("valor_max_uf_ha", String(filtros.valorMaxUfHa));
  }
  if (filtros.distanciaMaxKm !== undefined) {
    p.set("distancia_max_km", String(filtros.distanciaMaxKm));
  }

  if (filtros.incluirIncompletas) p.set("incluir_incompletas", "1");

  if (filtros.orden) p.set("orden", filtros.orden);
  if (filtros.pagina && filtros.pagina > 1) p.set("pagina", String(filtros.pagina));
  if (filtros.porPagina) p.set("por_pagina", String(filtros.porPagina));

  const cadena = p.toString();
  return cadena ? `?${cadena}` : "";
}

interface RespuestaDelCatalogo {
  data: Comparable[];
  meta: {
    total: number;
    pagina: number;
    por_pagina: number;
    quedan: number;
    mediana_uf_ha: number | null;
    uf: { valor: number; fecha: string } | null;
    clase: ClaseDeEvidencia;
    validadas: number[];
    en_la_papelera: number;
  };
}

export async function fetchCatalogo(
  informeId: number,
  filtros: FiltrosDelCatalogo = {},
): Promise<PaginaDelCatalogo> {
  const respuesta = await apiFetch<RespuestaDelCatalogo>(
    `/api/informes/${informeId}/referencias/catalogo${parametrosDelCatalogo(filtros)}`,
  );

  return {
    fichas: respuesta.data,
    total: respuesta.meta.total,
    pagina: respuesta.meta.pagina,
    porPagina: respuesta.meta.por_pagina,
    quedan: respuesta.meta.quedan,
    medianaUfHa: respuesta.meta.mediana_uf_ha,
    uf: respuesta.meta.uf,
    clase: respuesta.meta.clase,
    validadas: respuesta.meta.validadas ?? [],
    enLaPapelera: respuesta.meta.en_la_papelera ?? 0,
  };
}

/* ---------------------------------------------------------------------------
   Los puntos del mapa y del gráfico
   --------------------------------------------------------------------------- */

/**
 * Una ficha reducida a lo que se puede dibujar.
 *
 * No trae título, ni url, ni fuente, ni fecha: con miles de fichas eso
 * multiplicaría el peso para llenar tarjetas que nadie abre. Lo que el usuario
 * mira de verdad —una ficha concreta— lo sirve el listado, que ya la tiene.
 */
export interface PuntoDelCatalogo {
  id: number;
  lat: number | null;
  lng: number | null;
  /** UF por hectárea de terreno. `null` cuando no se pudo calcular. */
  uf_ha: number | null;
  /** Superficie de terreno en m². */
  m2: number | null;
}

export interface PuntosDelCatalogo {
  puntos: PuntoDelCatalogo[];
  /** Cuántas cumplen el filtro, se hayan dibujado o no. */
  total: number;
  /** Si el tope del servidor recortó la respuesta. */
  truncado: boolean;
  /**
   * Hasta dónde llega la pista del control de valor unitario.
   *
   * La mide el SERVIDOR sobre el conjunto SIN la banda de valor puesta, y por
   * eso no se puede deducir de `puntos`: esos ya vienen acotados por la banda,
   * así que medirla sobre ellos encogería la pista bajo la manija que la mueve
   * y el filtro se apretaría solo.
   *
   * `null` cuando no hay ninguna ficha con valor unitario calculable con el
   * resto de los filtros: ahí no hay mercado que dimensionar.
   */
  escalaUfHa: number | null;
}

interface RespuestaDePuntos {
  data: PuntoDelCatalogo[];
  meta: {
    total: number;
    dibujados: number;
    truncado: boolean;
    escala_uf_ha: number | null;
  };
}

/**
 * Todas las coordenadas del filtro, sin paginar.
 *
 * Usa los MISMOS filtros que el listado —de ahí que comparta
 * `parametrosDelCatalogo`— menos los de paginación, que acá no significan nada.
 */
export async function fetchPuntosDelCatalogo(
  informeId: number,
  filtros: FiltrosDelCatalogo = {},
): Promise<PuntosDelCatalogo> {
  const { pagina: _pagina, porPagina: _porPagina, ...sinPaginar } = filtros;

  const respuesta = await apiFetch<RespuestaDePuntos>(
    `/api/informes/${informeId}/referencias/puntos${parametrosDelCatalogo(sinPaginar)}`,
  );

  return {
    puntos: respuesta.data,
    total: respuesta.meta.total,
    truncado: respuesta.meta.truncado,
    escalaUfHa: respuesta.meta.escala_uf_ha ?? null,
  };
}

/**
 * Con la casilla "datos incompletos" los contadores incluyen los centroides de
 * comuna: el conteo existe para prometer lo que la lista devuelve, y la lista
 * los trae bajo ella.
 */
export function fetchCatalogosDelPaso2(
  informeId: number,
  clase: ClaseDeEvidencia,
  incluirIncompletas = false,
): Promise<CatalogosDelPaso2> {
  const casilla = incluirIncompletas ? "&incluir_incompletas=1" : "";

  return apiFetch<CatalogosDelPaso2>(
    `/api/informes/${informeId}/referencias/catalogos?clase=${clase}${casilla}`,
  );
}

/**
 * El valor que el informe se lleva del mercado, YA GUARDADO por el servidor.
 *
 * Lo calcula y lo persiste `CalculadorDelValorDeReferencia`; acá solo se lee.
 * La pantalla igual sabe calcularlo —para mover la cifra en el acto mientras el
 * tasador elige, sin esperar la respuesta— pero lo que vale es esto: es lo que
 * consumirá el resto del informe.
 */
export interface ValorDeReferencia {
  uf_ha_media: number;
  /** `null` cuando el informe todavía no declaró superficie. Nunca cero. */
  valor_total_uf: number | null;
  superficie_ha: number | null;
  /**
   * Cuántas fichas entraron de verdad: las que no tienen valor unitario
   * calculable quedan fuera, y sin este número una media sobre 3 de 10 se
   * leería como si fueran las diez.
   */
  referencias_consideradas: number;
}

export interface ReferenciasDelInforme {
  referencias: ReferenciaDelInforme[];
  total: number;
  validadas: number;
  enLaPapelera: number;
  /**
   * Indexado por clase. Una clase SIN comparables con valor NO viene, en vez de
   * llegar en cero: el informe no tiene un valor desconocido para ese mercado,
   * no tiene ninguno.
   */
  valoresDeReferencia: Partial<Record<ClaseDeEvidencia, ValorDeReferencia>>;
}

interface RespuestaDeReferencias {
  data: ReferenciaDelInforme[];
  meta: {
    total: number;
    validadas: number;
    en_la_papelera: number;
    valores_de_referencia: Partial<Record<ClaseDeEvidencia, ValorDeReferencia>>;
  };
}

export async function fetchReferenciasDelInforme(
  informeId: number,
): Promise<ReferenciasDelInforme> {
  const respuesta = await apiFetch<RespuestaDeReferencias>(
    `/api/informes/${informeId}/referencias`,
  );

  return {
    referencias: respuesta.data,
    total: respuesta.meta.total,
    validadas: respuesta.meta.validadas,
    enLaPapelera: respuesta.meta.en_la_papelera,
    // `?? {}` para el caso de un backend anterior a la columna: la pantalla
    // sigue mostrando su cálculo propio en vez de quedarse en blanco.
    valoresDeReferencia: respuesta.meta.valores_de_referencia ?? {},
  };
}

/** La identidad de una ficha: el PAR, porque el número solo no la distingue. */
export interface IdentidadDeComparable {
  clase: ClaseDeEvidencia;
  origen_id: number;
}

export interface ResultadoDeEnlace {
  enlazadas: ReferenciaDelInforme[];
  /** Cuántas ya estaban: un doble clic no es un error. */
  yaEstaban: number;
  /** Cuántas están en la papelera y NO se restauraron solas. */
  enLaPapelera: number;
  /** Las que el catálogo ya no entrega. */
  noDisponibles: IdentidadDeComparable[];
}

/**
 * Enlaza referencias al informe.
 *
 * Manda SOLO LA IDENTIDAD. Los valores los relee el servidor del catálogo y los
 * congela él: si el precio viajara desde acá, cualquiera podría dejar una cifra
 * inventada dentro de un informe pericial.
 *
 * La casilla "datos incompletos" tampoco es un valor: dice con qué calidad
 * releer, y viaja solo encendida para que una ficha ofrecida bajo ella no se
 * vea "no disponible" al agregarla.
 */
export async function enlazarReferencias(
  informeId: number,
  referencias: IdentidadDeComparable[],
  incluirIncompletas = false,
): Promise<ResultadoDeEnlace> {
  const respuesta = await apiFetch<{
    data: ReferenciaDelInforme[];
    meta: {
      enlazadas: number;
      ya_estaban: number;
      en_la_papelera: number;
      no_disponibles: IdentidadDeComparable[];
    };
  }>(`/api/informes/${informeId}/referencias`, {
    method: "POST",
    body: JSON.stringify(
      incluirIncompletas ? { referencias, incluir_incompletas: true } : { referencias },
    ),
  });

  return {
    enlazadas: respuesta.data,
    yaEstaban: respuesta.meta.ya_estaban,
    enLaPapelera: respuesta.meta.en_la_papelera,
    noDisponibles: respuesta.meta.no_disponibles,
  };
}

/**
 * La clave con que se identifica una ficha en una selección.
 *
 * Es el PAR y no el número: `listing.id` y `transaccion.id` son secuencias
 * independientes, así que una oferta y una transacción pueden compartirlo. Un
 * `Set` de números confundiría una con la otra.
 */
export function claveDeComparable(ficha: IdentidadDeComparable): string {
  return `${ficha.clase}:${ficha.origen_id}`;
}

/* ---------------------------------------------------------------------------
   Lo que se decide sobre una referencia YA ENLAZADA.

   Tres actos independientes: validar (revisé los datos), descartar (no sirve
   para esta tasación) y vaciar la papelera, que es el único irreversible.
--------------------------------------------------------------------------- */

/**
 * Marca —o desmarca— que una persona revisó los datos de esta referencia.
 *
 * Manda SIEMPRE el valor, nunca un endpoint por dirección: "no vino el campo"
 * no puede significar `false`, o desvalidaría en silencio lo que alguien
 * revisó.
 */
export async function validarReferencia(
  informeId: number,
  referenciaId: number,
  validada: boolean,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/${referenciaId}`,
    { method: "PATCH", body: JSON.stringify({ validada }) },
  );

  return respuesta.data;
}

/** A la papelera de ESTE informe. No borra nada y no toca el catálogo. */
export async function descartarReferencia(
  informeId: number,
  referenciaId: number,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/${referenciaId}`,
    { method: "DELETE" },
  );

  return respuesta.data;
}

export async function restaurarReferencia(
  informeId: number,
  referenciaId: number,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/${referenciaId}/restaurar`,
    { method: "POST" },
  );

  return respuesta.data;
}

/**
 * Con qué categorías se homologa esta referencia.
 *
 * MANDA LA LISTA ENTERA, no lo que cambió: `AjustadorDeReferencia` reemplaza
 * los ajustes de la ficha en vez de acumularlos, así que la lista recibida ES
 * el estado final. Quitar un ajuste es mandarla sin él.
 *
 * EL CUERPO LLEVA IDENTIDAD Y NUNCA VALORES, misma regla que al enlazar: el
 * porcentaje lo relee el servidor del catálogo. Viajando desde el navegador,
 * cualquiera podría congelar un −80 % donde la escala llega a −20, en un
 * informe pericial, y la fila se vería idéntica a una legítima.
 */
export async function ajustarReferencia(
  informeId: number,
  referenciaId: number,
  categoriaIds: number[],
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/${referenciaId}/ajustes`,
    {
      method: "PUT",
      body: JSON.stringify({
        ajustes: categoriaIds.map((categoria_id) => ({ categoria_id })),
      }),
    },
  );

  return respuesta.data;
}

/* ---------------------------------------------------------------------------
   Lo que se decide sobre una ficha DEL CATÁLOGO, que todavía no es del informe.

   SE DIRECCIONAN POR (CLASE, ORIGEN) Y NO POR ID, y no hay otra manera: la
   ficha vive en la otra base y de este lado todavía no existe ninguna fila que
   nombrar. La crea el propio pedido, en el momento en que el tasador decide
   algo.

   La CLASE viaja siempre junto al número porque `listing.id` y `transaccion.id`
   son secuencias independientes: sin ella, validar la transacción 101 marcaría
   la oferta 101, sin error y sin aviso.
--------------------------------------------------------------------------- */

/**
 * Marca —o desmarca— que se revisaron los datos de una ficha del catálogo.
 *
 * NO LA AGREGA AL INFORME NI LA RESERVA: la ficha sigue en el listado y sigue
 * habiendo que elegirla. Validar es una lectura sobre el dato de un tercero,
 * no un paso del flujo de selección.
 */
export async function validarFichaDelCatalogo(
  informeId: number,
  ficha: IdentidadDeComparable,
  validada: boolean,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/catalogo/${ficha.clase}/${ficha.origen_id}`,
    { method: "PUT", body: JSON.stringify({ validada }) },
  );

  return respuesta.data;
}

/** A la papelera de ESTE informe, sin haberla agregado. No toca el catálogo. */
export async function descartarFichaDelCatalogo(
  informeId: number,
  ficha: IdentidadDeComparable,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/catalogo/${ficha.clase}/${ficha.origen_id}`,
    { method: "DELETE" },
  );

  return respuesta.data;
}

export interface PapeleraDeReferencias {
  referencias: ReferenciaDelInforme[];
  total: number;
}

export async function fetchPapelera(informeId: number): Promise<PapeleraDeReferencias> {
  const respuesta = await apiFetch<{
    data: ReferenciaDelInforme[];
    meta: { total: number };
  }>(`/api/informes/${informeId}/referencias/papelera`);

  return { referencias: respuesta.data, total: respuesta.meta.total };
}

/**
 * Vaciar la papelera. IRREVERSIBLE, y con una consecuencia que no se ve: las
 * fichas borradas vuelven a ofrecerse en el catálogo, porque lo que las
 * mantenía fuera era justamente la fila.
 *
 * Devuelve cuántas borró: después de una acción que no se deshace, quedarse
 * mudo es lo peor que se puede hacer.
 */
export async function vaciarPapelera(informeId: number): Promise<number> {
  const respuesta = await apiFetch<{ meta: { borradas: number } }>(
    `/api/informes/${informeId}/referencias/papelera`,
    { method: "DELETE" },
  );

  return respuesta.meta.borradas;
}

/* ---------------------------------------------------------------------------
   Corregir el DATO del comparable.

   Es la única escritura del módulo que sale de este informe. Validar, descartar
   y homologar son juicios sobre ESTA tasación; corregir arregla el dato, y el
   dato es de todos: la corrección la ve el siguiente tasador que abra esa misma
   ficha del catálogo.

   ACÁ SÍ VIAJAN VALORES, al revés que al enlazar y al homologar. En aquellos el
   cuerpo manda identidad y los números los relee el servidor, porque dejarlos
   entrar de afuera permitiría congelar una cifra inventada. Corregir es lo
   contrario por definición: el valor que la persona escribió ES el dato, y no
   hay ninguna otra parte de donde sacarlo. Lo que sustituye a esa protección es
   que todo queda atribuido —quién corrigió, cuándo y qué decía antes— y que el
   servidor compara contra la ficha viva para saber qué cambió de verdad.
--------------------------------------------------------------------------- */

/**
 * Lo que el formulario del modal manda: la ficha ENTERA, no lo que cambió.
 *
 * El servidor decide qué se dio por corregido comparando contra el catálogo. Es
 * la misma forma que ya tiene `ajustarReferencia`, y por el mismo motivo: el
 * navegador no puede ser quien lo decida, porque no ve la ficha viva — la copia
 * que tiene en pantalla ya lleva puestas las correcciones anteriores.
 *
 * NO INCLUYE LOS VALORES UNITARIOS. Salen de dividir el valor total por la
 * superficie, así que mandarlos dejaría que las tres cifras se contradigan en
 * un informe pericial.
 */
export interface CorreccionDeComparable {
  titulo: string | null;
  descripcion: string | null;
  comuna_codigo: string | null;
  referencia: string | null;
  fuente: string | null;
  superficie_terreno_m2: number | null;
  precio_monto: number | null;
  precio_moneda: string | null;
}

export async function corregirComparable(
  informeId: number,
  referenciaId: number,
  correccion: CorreccionDeComparable,
): Promise<ReferenciaDelInforme> {
  const respuesta = await apiFetch<{ data: ReferenciaDelInforme }>(
    `/api/informes/${informeId}/referencias/${referenciaId}/correccion`,
    { method: "PUT", body: JSON.stringify(correccion) },
  );

  return respuesta.data;
}
